import { useEffect, useState } from 'react';

import useSWRInfinite from 'swr/infinite';

// Hooks
import { useIsFirstStatsLoaded } from '@/hooks/socialprofiles/useIsFirstStatsLoaded';

// Utils
import { get } from '@/utils/client';

// API Endpoints
import { MEDIAS } from '@/lib/routes/apiEndpoints';

const getKey = (
    pageIndex,
    previousPageData,
    activeSPID,
    limit,
    requestParams,
    endpoint = MEDIAS,
    competitorID,
    isCompetitorRequest
) => {
    // No request params
    if (!requestParams || (!competitorID && isCompetitorRequest)) return null;

    // Reached the end
    if (previousPageData && !previousPageData.medias) return null;

    // First page, we don't have `previousPageData`
    if (pageIndex === 0) {
        return [
            `${endpoint.replace(
                '%SPID%',
                competitorID ? competitorID : activeSPID
            )}?per_page=${limit}`,
            JSON.stringify(requestParams)
        ];
    }
    // Add the cursor to the API endpoint
    return [
        `${endpoint.replace(
            '%SPID%',
            competitorID ? competitorID : activeSPID
        )}?per_page=${limit}&cursor=${previousPageData.cursor}`,
        JSON.stringify(requestParams)
    ];
};

const useMedias = ({
    activeSPID,
    limit = 20,
    params = null,
    options = {},
    endpoint = undefined,
    isCompetitorRequest = false,
    competitorID = null
} = {}) => {
    const [requestParams, setRequestParams] = useState(params);
    const [activeSocialProfile, setActiveSocialProfile] = useState(activeSPID);
    const isFirstStatsLoaded = useIsFirstStatsLoaded({ SPID: activeSPID });

    useEffect(() => {
        setActiveSocialProfile(activeSPID);
    }, [activeSPID]);

    const {
        data: dataMedias,
        size,
        setSize,
        mutate: mutateMedias,
        error: mediasError
    } = useSWRInfinite(
        (pageIndex, previousPageData) =>
            isFirstStatsLoaded
                ? getKey(
                      pageIndex,
                      previousPageData,
                      activeSocialProfile,
                      limit,
                      requestParams,
                      endpoint,
                      competitorID,
                      isCompetitorRequest
                  )
                : null,
        ([url]) => get(url, requestParams).then((res) => res.data),
        {
            shouldRetryOnError: false,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            ...options
        }
    );

    const isLoadingInitialData = !dataMedias && !mediasError;
    const isLoadingMore = size > 0 && dataMedias && typeof dataMedias[size - 1] === 'undefined';

    return {
        dataMedias,
        loadMoreMedia: () => setSize(size + 1), // Function to load more media (load more button)
        mutateMedias,
        mediasError,
        requestParams,
        setRequestParams, // Setter to change parameters in the url
        setActiveSocialProfile,
        isLoadingInitialData, // Check if it's the initial loading (not the load more)
        isLoadMore: dataMedias && typeof dataMedias[dataMedias.length - 1].cursor === 'string', // Check if there is a cursor to active the load more button
        isLoadingMore // Check if the load more is loading
    };
};

export default useMedias;
